import type { InternalOpsChannel } from "@/lib/api/internal-ops-messaging";

export type SupportDirectMember = NonNullable<
  InternalOpsChannel["members"]
>[number];

export type SupportDirectRole = "supervisor" | "other";

export interface SupportDirectRoleContext {
  currentUserId: string;
  isCurrentUserMentor: boolean;
  activeSupervisorId: string | null;
}

/**
 * Single canonical supervisor-vs-other decision for a `direct` internal-ops
 * channel, shared by legacy `/conversations` and Notice; rule precedence matters.
 */
export function resolveSupportDirectRole(
  otherMember: SupportDirectMember | undefined,
  ctx: SupportDirectRoleContext,
): SupportDirectRole {
  if (otherMember?.user?.deactivated) {
    return "other";
  }

  const isMyActiveSupervisor =
    ctx.isCurrentUserMentor &&
    ctx.activeSupervisorId !== null &&
    otherMember?.user_id === ctx.activeSupervisorId;
  if (isMyActiveSupervisor) {
    return "supervisor";
  }

  // @todo - might need eyes on it once we finalise supervisor stuff
  const isSupervisorUser =
    !!otherMember?.user?.system_roles?.includes("supervisor");

  if (ctx.isCurrentUserMentor && isSupervisorUser) {
    return "other";
  }
  if (isSupervisorUser) {
    return "supervisor";
  }

  return "other";
}

// Shared by legacy `/conversations` and Notice. Fine-grained direct roles are
// kept distinct here so each consumer can fold them into its own bucket shape
// (Notice collapses them into "other", legacy keeps them separate).
export type SupportChannelBucket =
  | "supervisors"
  | "mentors"
  | "leadMentors"
  | "admins"
  | "cohorts"
  | "groups"
  | "other";

export function classifySupportChannel(
  internal: InternalOpsChannel | undefined,
  ctx: SupportDirectRoleContext,
): SupportChannelBucket {
  if (!internal) {
    return "other";
  }

  switch (internal.kind) {
    case "cohort":
      return "cohorts";
    case "group":
      return "groups";
    case "direct": {
      const otherMember = internal.members?.find(
        (member) => member.user_id !== ctx.currentUserId,
      );
      if (resolveSupportDirectRole(otherMember, ctx) === "supervisor") {
        return "supervisors";
      }
      if (
        otherMember?.user?.deactivated ||
        otherMember?.user?.system_roles?.includes("supervisor")
      ) {
        return "other";
      }
      const role = otherMember?.user?.role?.toLowerCase();
      if (role === "mentor") return "mentors";
      if (role === "lead_mentor") return "leadMentors";
      if (role === "admin" || role === "superadmin") return "admins";
      return "other";
    }
    default: {
      const exhaustiveCheck: never = internal.kind;
      return exhaustiveCheck;
    }
  }
}
